/**
 * CheckForNewClientsButton
 *
 * Staging-only button that triggers the Salesforce Data Sync force-sync API
 * for Account and its credit-related child objects so L2 can push both the
 * rows and the Account relationships into Gobodo in a single click.
 * Surfaces the 202 executionArn and any error verbatim from the API.
 * Does not poll SFN; operators can copy the ARN to check status in AWS console.
 *
 * Gated by REACT_APP_ENABLE_FORCE_SYNC. Hidden entirely when disabled so
 * prod builds show nothing even if this component is imported.
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, RefreshCcw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import {
  isForceSyncEnabled,
  triggerForceSync,
  type ForceSyncResult,
} from '../../services/forceSyncService';

type Phase = 'idle' | 'triggering' | 'accepted' | 'error';

interface LastResult {
  phase: Phase;
  executionArn?: string;
  message?: string;
  at?: string;
}

const REFRESH_QUERY_KEYS = [
  ['clientListView'],
  ['clientListViewData'],
  ['clientListViews'],
  ['creditBlockClients'],
  ['creditBlockStats'],
];

const SYNC_OBJECT_TYPES = [
  'Account',
  'CreditBlock__c',
  'Credit_Bureaus__c',
  'Array_Subscription__c',
];

export function CheckForNewClientsButton(): JSX.Element | null {
  const enabled = isForceSyncEnabled();
  const { email } = useCurrentUser();
  const queryClient = useQueryClient();
  const [state, setState] = useState<LastResult>({ phase: 'idle' });

  if (!enabled) {
    return null;
  }

  const busy = state.phase === 'triggering';

  const handleClick = async (): Promise<void> => {
    setState({ phase: 'triggering' });

    const result: ForceSyncResult = await triggerForceSync({
      objectTypes: SYNC_OBJECT_TYPES,
      syncType: 'delta',
      metadata: {
        requestedBy: email || 'unknown',
        reason: 'team-dashboard:check-new-clients',
      },
    });

    if (result.ok) {
      setState({
        phase: 'accepted',
        executionArn: result.executionArn,
        at: new Date().toISOString(),
      });
      REFRESH_QUERY_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    } else {
      setState({
        phase: 'error',
        message: result.message,
        at: new Date().toISOString(),
      });
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!state.executionArn) return;
    try {
      await navigator.clipboard.writeText(state.executionArn);
    } catch {
      // Clipboard unavailable (older browsers, non-secure contexts).
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        title="Triggers a delta sync of Accounts, Credit Blocks, Credit Bureaus, and Array Subscriptions from Salesforce. Staging only."
        className={[
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
          'border transition-colors',
          'bg-indigo-50 text-indigo-700 border-indigo-200',
          'hover:bg-indigo-100 hover:border-indigo-300',
          'dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
          'dark:hover:bg-indigo-900/40',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1',
        ].join(' ')}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCcw className="h-4 w-4" />
        )}
        <span>{busy ? 'Checking…' : 'Check for new clients'}</span>
      </button>

      {state.phase === 'accepted' && state.executionArn && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="font-medium">Sync started.</span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 underline decoration-dotted hover:decoration-solid"
            title={state.executionArn}
          >
            <Copy className="h-3 w-3" />
            Copy ARN
          </button>
        </div>
      )}

      {state.phase === 'error' && state.message && (
        <div className="flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400 max-w-xs text-right">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{state.message}</span>
        </div>
      )}
    </div>
  );
}
